import React, { useState, useRef, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "../../services/api";
import { toast } from "react-toastify";
import ASSETS from "../../data/assets";
import { getAssetUrl } from "../../services/helper";

const EditLiveRoomModal = ({ isOpen, onClose, roomId }) => {
  const [roomName, setRoomName] = useState("");
  const [description, setDescription] = useState("");
  const [members, setMembers] = useState([]);
  const [isPrivate, setIsPrivate] = useState(true);
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(ASSETS.profileCover);
  const [copied, setCopied] = useState(false);
  const [searchInput, setSearchInput] = useState("");

  const fileRef = useRef();
  const queryClient = useQueryClient();

  // Fetch existing room data
  const { data: roomData, isLoading } = useQuery({
    queryKey: ["liveRoom", roomId],
    queryFn: () => api.getLiveRoomById(roomId),
    enabled: !!roomId && isOpen,
  });

  // Search users to add as members
  const { data: searchResults, isLoading: isSearching } = useQuery({
    queryKey: ["search-contact-members", searchInput],
    queryFn: () => api.fetchConnectedContact(searchInput),
    enabled: searchInput.length > 1,
    staleTime: 1000 * 60 * 5,
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (formData) => api.updateLiveRoom(roomId, formData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["liveRoom", roomId] });
      queryClient.invalidateQueries({ queryKey: ["all_chats"] });
      toast.success("Live Room updated successfully!");
      onClose();
    },
    onError: (error) => {
      console.error("Failed to update room:", error);
      toast.error("Failed to update live room. Please try again.");
    },
  });

  // Populate form when data is loaded
  useEffect(() => {
    if (roomData) {
      const room = roomData.data || roomData;

      setRoomName(room.name || "");
      setDescription(room.description || "");
      setIsPrivate(!!room.is_private || room.is_private === 1);
      setAvatarPreview(room.avatar || ASSETS.profileCover);

      if (room.members?.length > 0) {
        setMembers(room.members);
      }
    }
  }, [roomData]);

  if (!isOpen) return null;

  const removeMember = (id) => {
    setMembers(members.filter((m) => m.id !== id));
  };

  const addMember = (user) => {
    if (!members.find((m) => m.id === user.id)) {
      setMembers([...members, user]);
    }
    setSearchInput("");
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setAvatarFile(file);
      setAvatarPreview(URL.createObjectURL(file));
    }
  };

  const handleCopy = () => {
    const link = roomData?.data?.inviteLink || roomData?.inviteLink;
    if (link) {
      navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleSave = () => {
    if (!roomName.trim()) {
      toast.error("Room name is required");
      return;
    }

    const formData = new FormData();
    formData.append("name", roomName);
    formData.append("description", description);
    formData.append("is_private", isPrivate ? 1 : 0);
    if (avatarFile) formData.append("avatar", avatarFile);
    formData.append("user_ids", JSON.stringify(members.map((m) => m.id)));

    updateMutation.mutate(formData);
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
        <div className="text-white">Loading room data...</div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      <div className="relative bg-white rounded-xl sm:rounded-3xl w-full max-w-[644px] shadow-2xl flex flex-col max-h-[95vh] sm:max-h-[90vh] animate-fade-in-up">
        {/* Header */}
        <div className="flex-shrink-0 px-4 sm:px-6 pt-4 sm:pt-6 pb-2 border-b border-gray-100">
          <h2 className="text-[20px] sm:text-[22px] font-medium text-[#111827]" style={{ fontFamily: "Inter" }}>
            Edit Live Room
          </h2>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 sm:py-2">
          {/* Profile Picture */}
          <label className="block mb-2 sm:mb-3 text-[15px] sm:text-[17px] font-medium text-[#111827]" style={{ fontFamily: "Inter" }}>
            Profile Picture
          </label>

          <div className="relative mb-4 sm:mb-5 cursor-pointer inline-block" onClick={() => fileRef.current?.click()}>
            <div
              className="rounded-full overflow-hidden bg-gray-100"
              style={{ width: "80px", height: "80px", border: "4px solid #1F3A60", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}
            >
              <img src={getAssetUrl(avatarPreview)} alt="Room" className="w-full h-full object-cover" />
            </div>
            <div
              className="absolute bottom-0 right-0 rounded-full flex items-center justify-center shadow-md"
              style={{ width: "28px", height: "28px", backgroundColor: "#1F3A60" }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
            </div>
          </div>

          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange} />

          {/* Room Name */}
          <label className="block mb-2 sm:mb-3 text-[15px] sm:text-[17px] font-medium text-[#111827]" style={{ fontFamily: "Inter" }}>
            Live Room Name
          </label>
          <input
            value={roomName}
            onChange={(e) => setRoomName(e.target.value)}
            className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-200 rounded-xl text-[14px] sm:text-[16px] font-normal outline-none focus:border-[#1F3A60] focus:ring-2 focus:ring-[#1F3A60]/20 bg-[#F9FAFB] hover:bg-white transition mb-4 sm:mb-5"
            style={{ fontFamily: "Inter" }}
          />

          {/* Description */}
          <label className="block mb-2 sm:mb-3 text-[15px] sm:text-[17px] font-medium text-[#111827]" style={{ fontFamily: "Inter" }}>
            Description
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-200 rounded-xl text-[14px] sm:text-[16px] font-normal outline-none focus:border-[#1F3A60] focus:ring-2 focus:ring-[#1F3A60]/20 bg-[#F9FAFB] hover:bg-white transition mb-4 sm:mb-5 min-h-[80px]"
            style={{ fontFamily: "Inter" }}
          />

          {/* Members Search */}
          <label className="block mb-2 sm:mb-3 text-[15px] sm:text-[17px] font-medium text-[#111827]" style={{ fontFamily: "Inter" }}>
            Add Members
          </label>

          <div className="relative mb-3">
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search users by username or name..."
              className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-200 rounded-xl text-[14px] sm:text-[16px] font-normal outline-none focus:border-[#1F3A60] focus:ring-2 focus:ring-[#1F3A60]/20 bg-[#F9FAFB] hover:bg-white transition"
              style={{ fontFamily: "Inter" }}
            />

            {searchInput.length > 1 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-auto">
                {isSearching ? (
                  <div className="p-3 text-gray-500 text-sm">Searching...</div>
                ) : searchResults?.data?.length > 0 ? (
                  searchResults.data.map((user) => (
                    <div
                      key={user.id}
                      onClick={() => addMember(user)}
                      className="px-4 py-3 hover:bg-gray-100 cursor-pointer flex items-center gap-3 border-b last:border-none"
                    >
                      <div className="font-medium text-[14px]" style={{ fontFamily: "Inter" }}>
                        {user.name || user.username}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="p-3 text-gray-500 text-sm">No users found</div>
                )}
              </div>
            )}
          </div>

          {/* Selected Members */}
          <div className="border border-gray-200 rounded-xl p-2 sm:p-3 bg-[#F9FAFB] mb-4 sm:mb-6 min-h-[80px] sm:min-h-[90px]">
            <div className="flex flex-wrap gap-1.5 sm:gap-2">
              {members.map((member) => {
                const isCurrentUser = member.is_current_user === 1 || member.is_current_user === true;

                return (
                  <span
                    key={member.id}
                    className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1 sm:py-1.5 bg-white border border-gray-200 rounded-full text-[11px] sm:text-[13px] text-gray-700"
                    style={{ fontFamily: "Inter" }}
                  >
                    {member.name || member.username}

                    {/* Show "You" label for current user */}
                    {isCurrentUser && (
                      <span className="text-[10px] text-blue-600 font-medium">(You)</span>
                    )}

                    {/* Only show remove button if NOT current user */}
                    {!isCurrentUser && (
                      <button
                        type="button"
                        onClick={() => removeMember(member.id)}
                        className="text-gray-400 hover:text-red-600 transition-colors ml-1"
                      >
                        <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <line x1="18" y1="6" x2="6" y2="18" />
                          <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                      </button>
                    )}
                  </span>
                );
              })}
              {members.length === 0 && (
                <span className="text-gray-400 text-sm py-2">No members added yet</span>
              )}
            </div>
          </div>

          {/* Security Toggle */}
          <div className="flex items-center justify-between mb-4 sm:mb-8">
            <div>
              <h3 className="block text-[15px] sm:text-[17px] font-medium text-[#111827]" style={{ fontFamily: "Inter" }}>
                Security
              </h3>
              <p className="text-[12px] sm:text-[14px] font-normal text-[#6B7280]" style={{ fontFamily: "Inter" }}>
                Private
              </p>
            </div>
            <button
              type="button"
              onClick={() => setIsPrivate(!isPrivate)}
              className={`relative w-[52px] h-[28px] rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-[#1F3A60]/30 ${isPrivate ? "bg-[#1F3A60]" : "bg-[#D1D5DB]"
                }`}
            >
              <span
                className={`absolute top-[2px] left-[2px] w-[24px] h-[24px] bg-white rounded-full shadow-md transition-all duration-300 ${isPrivate ? "translate-x-[24px]" : "translate-x-0"
                  }`}
              />
            </button>
          </div>

          {/* Share Link */}
          <div className="mb-4 sm:mb-6">
            <p className="text-[12px] sm:text-[16px] text-gray-500 leading-relaxed break-all mb-2 sm:mb-3" style={{ fontFamily: "Inter" }}>
              {roomData?.data?.inviteLink || roomData?.inviteLink || "No invite link available"}
            </p>
            <div className="flex justify-end gap-2 sm:gap-3">
              <button onClick={handleCopy} className="text-gray-400 hover:text-gray-700 transition" title="Copy link">
                {copied ? "✓ Copied" : "Copy"}
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 flex justify-start gap-2 sm:gap-3 px-4 sm:px-6 py-3 sm:py-4 border-t border-gray-100">
          <button
            onClick={onClose}
            className="flex-1 sm:flex-none px-4 sm:px-12 py-2.5 sm:py-3 border border-[#D1D5DB] rounded-lg text-[14px] sm:text-[16px] text-[#374151] hover:bg-gray-50 transition"
            style={{ fontFamily: "Inter" }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={updateMutation.isPending}
            className="flex-1 sm:flex-none px-4 sm:px-12 py-2.5 sm:py-3 bg-[#1F3A60] rounded-lg text-[14px] sm:text-[16px] text-white hover:bg-[#18304F] transition disabled:opacity-50"
            style={{ fontFamily: "Inter" }}
          >
            {updateMutation.isPending ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(24px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in-up { animation: fadeInUp 0.35s ease-out; }
      `}</style>
    </div>
  );
};

export default EditLiveRoomModal;